import React from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { ImageUploadField, ColorPicker } from '../common';

// 有効なカラーコードかどうかをチェックする関数
const isValidColorCode = (color: string): boolean => {
  if (!color) return false;
  // #rrggbb形式（6桁の16進数）またはrgba()形式をチェック
  return /^#[0-9A-Fa-f]{6}$/.test(color) || /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(color);
};

interface BackgroundSettingsSectionProps {
  backgroundImage?: string;
  backgroundColor?: string;
  pageBackgroundColor?: string;
  onBackgroundImageMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
  onBackgroundImageDrop: (file: File) => void;
  onBackgroundImageDelete: () => void;
  onBackgroundColorChange: (color: string) => void;
  onPageBackgroundColorChange: (color: string) => void;
}

export const BackgroundSettingsSection: React.FC<BackgroundSettingsSectionProps> = ({
  backgroundImage,
  backgroundColor,
  pageBackgroundColor,
  onBackgroundImageMenuClick,
  onBackgroundImageDrop,
  onBackgroundImageDelete,
  onBackgroundColorChange,
  onPageBackgroundColorChange,
}) => {
  // カラーコード入力フィールドのローカル状態（入力中の不完全な値も保持）
  const [backgroundColorInput, setBackgroundColorInput] = React.useState<string>(backgroundColor || '#ffffff');
  const [pageBackgroundColorInput, setPageBackgroundColorInput] = React.useState<string>(pageBackgroundColor || '#ffffff');
  
  // エラーメッセージ
  const [backgroundColorError, setBackgroundColorError] = React.useState<string | null>(null);
  const [pageBackgroundColorError, setPageBackgroundColorError] = React.useState<string | null>(null);


  // 親から渡された値が変更された時、ローカル状態を更新（有効な値の場合のみ）
  React.useEffect(() => {
    if (backgroundColor && isValidColorCode(backgroundColor)) {
      setBackgroundColorInput(backgroundColor);
      setBackgroundColorError(null);
    }
  }, [backgroundColor]);

  React.useEffect(() => {
    if (pageBackgroundColor && isValidColorCode(pageBackgroundColor)) {
      setPageBackgroundColorInput(pageBackgroundColor);
      setPageBackgroundColorError(null);
    }
  }, [pageBackgroundColor]);

  // 背景色変更ハンドラー（有効な値の場合のみ親コンポーネントに通知）
  const handleBackgroundColorChange = (value: string) => {
    // ローカル状態を更新
    setBackgroundColorInput(value);
    setBackgroundColorError(null);
    
    // 有効な値の場合のみ親コンポーネントに通知
    if (isValidColorCode(value)) {
      onBackgroundColorChange(value);
    }
  };

  // 背景色のバリデーション（フォーカスが外れた時）
  const handleBackgroundColorBlur = () => {
    const currentValue = backgroundColorInput || '#ffffff';
    // 無効な値でも親コンポーネントに通知（保存時のバリデーション用）
    onBackgroundColorChange(currentValue);
    
    if (!isValidColorCode(currentValue)) {
      setBackgroundColorError('無効なカラーコードです。有効な形式: #rrggbb（例: #ffffff）');
    } else {
      setBackgroundColorError(null);
    }
  };

  // ページの背景色変更ハンドラー（有効な値の場合のみ親コンポーネントに通知）
  const handlePageBackgroundColorChange = (value: string) => {
    // ローカル状態を更新
    setPageBackgroundColorInput(value);
    setPageBackgroundColorError(null);
    
    // 有効な値の場合のみ親コンポーネントに通知
    if (isValidColorCode(value)) {
      onPageBackgroundColorChange(value);
    }
  };

  // ページの背景色のバリデーション（フォーカスが外れた時）
  const handlePageBackgroundColorBlur = () => {
    const currentValue = pageBackgroundColorInput || '#ffffff';
    // 無効な値でも親コンポーネントに通知（保存時のバリデーション用）
    onPageBackgroundColorChange(currentValue);
    
    if (!isValidColorCode(currentValue)) {
      setPageBackgroundColorError('無効なカラーコードです。有効な形式: #rrggbb（例: #ffffff）');
    } else {
      setPageBackgroundColorError(null);
    }
  };

  // カラーピッカーに渡す値（有効な値のみ）
  const validBackgroundColor = (backgroundColor && isValidColorCode(backgroundColor)) ? backgroundColor : '#ffffff';
  const validPageBackgroundColor = (pageBackgroundColor && isValidColorCode(pageBackgroundColor)) ? pageBackgroundColor : '#ffffff';

  return (
    <Box sx={{ mb: 3 }}>
      {/* PCビューとSPビューを横並び */}
      <Box sx={{ display: 'flex', gap: 3 }}>
      {/* PCビュー */}
        <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2, fontSize: '16px' }}>
          PCビュー:
        </Typography>

        {/* 背景画像と背景色を横並び */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
          {/* 背景画像 */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#000000', fontWeight: 500 }}>
                背景画像
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '11px', color: '#999999' }}>
                JPEG, PNG, WebP, GIF (最大100MB)
              </Typography>
            </Box>
            <ImageUploadField
              value={backgroundImage}
              onClick={onBackgroundImageMenuClick}
              onDrop={onBackgroundImageDrop}
              onDelete={onBackgroundImageDelete}
              alt="背景画像"
            />
          </Box>

          {/* 背景色 */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#000000', fontWeight: 500 }}>
                背景色
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '11px', color: '#999999' }}>
                ※設定は背景画像が優先されます
              </Typography>
            </Box>
            <ColorPicker
              value={validBackgroundColor}
              onChange={onBackgroundColorChange}
            />
          </Box>
        </Box>
      </Box>

      {/* SPビュー */}
        <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2, fontSize: '16px' }}>
          SPビュー:
        </Typography>

        {/* 背景色 */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', width: '100%' }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontSize: '14px', color: '#000000', fontWeight: 500 }}>
                  背景色
                </Typography>
              </Box>
              <ColorPicker
                value={validPageBackgroundColor}
                onChange={onPageBackgroundColorChange}
              />
            </Box>
            <Box sx={{ flex: 1 }}></Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
